<template>
  <div class="users-permissions" ref="usersPermissionsRef">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true" style="fill: #fff">
        <use xlink:href="#icon-a-shezhi2" />
      </svg>
    </breadcrumbs>
    <div class="users-permissions-content">
      <a-spin :spinning="loading">
        <artmisads-button class="add-new-user" type="primary" @click="addNewUser">{{ st('usersPermissionsPage.addNewUser') }}</artmisads-button>
        <artmisads-table :columns="columns" :data-source="data">
          <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'createDate'">
              {{ record.delegateUserCreateAt ? moment(record.delegateUserCreateAt).format('YYYY-MM-DD HH:mm:ss') : '-'
              }}
            </template>
            <template v-else-if="column.key === 'status'">
              <Status :type="record.status.toLowerCase()" :noBorder="true" :text="statusLabel(record.status)" />
            </template>
            <template v-else-if="column.key === 'actions'">
              <div class="actions">
                <artmisads-select-button type="link" :isSquare="true" size="small"
                  @click="() => handleEdit(record, index)">{{ st('usersPermissionsPage.edit') }}</artmisads-select-button>
                <a-popconfirm v-if="record.status !== 'PENDING'"
                  :title="record.status === 'ACTIVE' ? st('usersPermissionsPage.confirmDeactivateUser') : st('usersPermissionsPage.confirmActivateUser')"
                  @confirm="handleStatus(record, index)">
                  <artmisads-select-button type="link" class="status-btn" :isSquare="true" size="small">{{
                    record.status === 'ACTIVE' ? st('usersPermissionsPage.deactivate') : st('usersPermissionsPage.activate')
                  }}</artmisads-select-button>
                </a-popconfirm>
                <a-popconfirm :title="st('usersPermissionsPage.confirmDeleteUser')" @confirm="handleDelete(record, index)">
                  <artmisads-select-button class="delete" type="link" :isSquare="true"
                    size="small">{{ st('usersPermissionsPage.delete') }}</artmisads-select-button>
                </a-popconfirm>
              </div>
            </template>
          </template>
        </artmisads-table>
        <artmisads-modal v-model:open="open"
          :title="form.index > -1 ? st('usersPermissionsPage.modalTitleEdit') : st('usersPermissionsPage.modalTitleAdd')"
          style="min-height: 320px" @confirm="handleConfirm" @cancel="handleCancel" :maskClosable="false"
          :getContainer="() => $refs.usersPermissionsRef">
          <artmisads-form :model="form" layout="vertical" :rules="rules" ref="formRef">
            <a-form-item :label="st('usersPermissionsPage.labelAccountEmail')" name="email" :colon="false" v-bind="validateInfos.email">
              <artmisads-input v-model:value="form.email" :disabled="form.index > -1"
                :placeholder="st('usersPermissionsPage.placeholderEmail')" @blur="() => validate('email')" />
            </a-form-item>
            <a-form-item :label="st('usersPermissionsPage.labelUserName')" name="userName" :colon="false" v-bind="validateInfos.userName">
              <artmisads-input v-model:value="form.userName" :placeholder="st('usersPermissionsPage.placeholderUserName')" show-count
                @blur="() => validate('userName')" :maxlength="20" />
            </a-form-item>
            <a-form-item :label="st('usersPermissionsPage.labelPermission')" name="menuIds" :colon="false" v-bind="validateInfos.menuIds">
              <a-tree-select :treeDefaultExpandAll="true" :placeholder="st('usersPermissionsPage.placeholderSelect')" v-model:value="form.menuIds"
                tree-node-filter-prop="label" :getPopupContainer="() => $refs.usersPermissionsRef" treeCheckable
                allow-clear :tree-data="[menu]" @blur="() => validate('menuIds')" @change="handleMenuIdsChange"
                :field-names="{ children: 'children', label: 'name', key: 'id', value: 'id' }" />
            </a-form-item>
            <a-form-item name="brandIds" :colon="false" v-if="userType === 'seller'">
              <template #label>
                <span class="brands-permission-label">
                  {{ st('usersPermissionsPage.labelBrandsPermission') }}
                  <a-tooltip :title="st('usersPermissionsPage.brandsPermissionHelper')">
                    <Icon name="wenhao-tianchong" class="help-icon" />
                  </a-tooltip>
                </span>
              </template>
              <artmisads-select
                v-model:value="form.brandIds"
                mode="multiple"
                :options="brandOptions"
                :loading="brandLoading"
                :placeholder="st('usersPermissionsPage.placeholderSelect')"
                allow-clear
                show-search
                :filter-option="false"
                :get-popup-container="() => $refs.usersPermissionsRef"
                @search="handleBrandSearch"
                @popup-scroll="handleBrandPopupScroll"
              />
              <p class="brands-permission-helper">{{ st('usersPermissionsPage.brandsPermissionLeaveBlankHint') }}</p>
            </a-form-item>
          </artmisads-form>
          <template #footer>
            <artmisads-button type="default" @click="handleCancel">{{ st('common.cancel') }}</artmisads-button>
            <artmisads-button type="primary" @click="handleConfirm">{{
              form.index > -1 ? st('usersPermissionsPage.save') : st('usersPermissionsPage.sendInviteLink')
            }}</artmisads-button>
          </template>
        </artmisads-modal>
      </a-spin>
    </div>
  </div>
</template>

<script setup>
import moment from 'moment';
import { capitalize, countBrandNames } from '@/utils';
import { useRoute } from 'vue-router';
import registerApi from '@/server/register';
import { brandApi } from '@/server/index';
import Status from "@/components/Status.vue";
import Icon from '@/components/Icon.vue';
import { Form, message } from 'ant-design-vue';
import { ref, reactive, computed, onBeforeMount } from 'vue';
import breadcrumbs from "../seller/breadcrumbs.vue";
import { useUserInfoStore } from '@/storeData/index';
import { useSellerI18n } from '@/composables/useSellerI18n';

const route = useRoute();
const { st } = useSellerI18n();
const userType = route.path.startsWith('/publisher') ? 'publisher' : 'seller';
const breadcrumbsData = computed(() => ({
  title: st('menu.usersPermissions'),
  color: userType == 'publisher' ? "#FFD358" : "#776D91",
  links: [
    {
      name: st('common.home'),
      path: `/${userType}/dashboard`,
    },
    {
      name: st('menu.settings'),
      path:`${userType=='publisher'?'/publisher/settings/users-permissions':'/seller/connection'}` ,
    },
    {
      name: st('menu.usersPermissions'),
      path: `/${userType}/${userType == 'publisher' ? 'settings/' : ''}users-permissions`,
    },
  ],
}));

function statusLabel(status) {
  const u = String(status || '').toUpperCase();
  if (u === 'ACTIVE') return st('usersPermissionsPage.statusActive');
  if (u === 'INACTIVE') return st('usersPermissionsPage.statusInactive');
  if (u === 'PENDING') return st('usersPermissionsPage.statusPending');
  return capitalize(status || '');
}
const open = ref(false);
const formRef = ref(null);
const loading = ref(false);
const useForm = Form.useForm;
const parentMenu = new Set();
const store = useUserInfoStore();
const form = ref({
  email: '',
  menuIds: [],
  brandIds: [],
  userName: '',
});
const brandOptions = ref([]);
const brandRawList = ref([]);
const brandLoading = ref(false);
const brandHasMore = ref(true);
const brandSearchKeyword = ref('');
const brandPagination = reactive({
  page: 1,
  size: 20,
});
const rulesRef = reactive({
  userName: [{
    required: true,
    trigger: ['blur'],
    message: st('usersPermissionsPage.validationUserNameRequired'),
  }],
  email: [{
    required: true,
    message: st('usersPermissionsPage.validationEmailRequired'),
    trigger: ['change']
  }, {
    type: 'email',
    message: st('usersPermissionsPage.validationEmailInvalid'),
    trigger: ['blur']
  }],
  menuIds: [{
    required: true,
    message: st('usersPermissionsPage.validationMenuRequired'),
    trigger: ['change']
  }],
});
const { validate, validateInfos, resetFields } = useForm(form, rulesRef);
const addNewUser = () => {
  open.value = true;
  parentMenu.clear()
  form.value = {
    email: '',
    menuIds: [],
    brandIds: [],
    userName: '',
  }
  initBrandList();
};

const toBrandValue = (id) => {
  const num = Number(id);
  return Number.isNaN(num) ? id : num;
};

const normalizeRecordBrands = (record = {}) => {
  const raw =
    record.authorizedBrands ||
    record.brandList ||
    record.brands ||
    record.brandIds ||
    [];

  if (!Array.isArray(raw) || !raw.length) {
    return { brandIds: [], brandList: [] };
  }

  if (typeof raw[0] === 'object') {
    const brandList = raw;
    const brandIds = brandList
      .map((brand) => toBrandValue(brand.id ?? brand.brandId))
      .filter((id) => id != null && id !== '');
    return { brandIds, brandList };
  }

  return {
    brandIds: raw.map(toBrandValue).filter((id) => id != null && id !== ''),
    brandList: [],
  };
};

const fetchBrandsByIds = async (brandIds = []) => {
  if (!brandIds.length) return [];
  const responses = await Promise.all(
    brandIds.map((brandId) =>
      brandApi.getBrandDetail({ brandId }).catch(() => null),
    ),
  );
  return responses
    .filter((res) => res?.success && res?.data)
    .map((res) => res.data);
};

const getBrandLabel = (brand, nameCount) => {
  const amazonBrandName = brand.amazonBrandName || brand.brandName || brand.name || '';
  if (!amazonBrandName) return String(brand.id ?? brand.brandId);
  if (nameCount[amazonBrandName] > 1) {
    const amazonName = brand.amazonProfileAccountInfoName || '';
    return amazonName ? `${amazonBrandName}(${amazonName})` : amazonBrandName;
  }
  return amazonBrandName;
};

const buildBrandOptions = (brands) => {
  const nameCount = countBrandNames(brands, 'amazonBrandName');
  return brands.map((brand) => ({
    label: getBrandLabel(brand, nameCount),
    value: toBrandValue(brand.id ?? brand.brandId),
  }));
};

const mergeBrandRows = (newRows, reset = false) => {
  if (reset) {
    brandRawList.value = newRows;
  } else {
    const rowMap = new Map(
      brandRawList.value.map((brand) => [String(brand.id ?? brand.brandId), brand]),
    );
    newRows.forEach((brand) => rowMap.set(String(brand.id ?? brand.brandId), brand));
    brandRawList.value = Array.from(rowMap.values());
  }
  brandOptions.value = buildBrandOptions(brandRawList.value);
};

const resetBrandList = () => {
  brandRawList.value = [];
  brandOptions.value = [];
  brandHasMore.value = true;
  brandPagination.page = 1;
  brandSearchKeyword.value = '';
};

const seedSelectedBrandOptions = (brandIds = [], brandList = []) => {
  if (!brandIds.length) return;
  const brandMap = new Map(
    (brandList || []).map((brand) => [String(brand.id ?? brand.brandId), brand]),
  );
  const rows = brandIds.map((id) => {
    const brand = brandMap.get(String(id));
    return brand || { id: toBrandValue(id), amazonBrandName: String(id) };
  });
  mergeBrandRows(rows, true);
};

const fetchBrandOptions = async (page = 1, keyword = brandSearchKeyword.value, reset = false) => {
  if (userType !== 'seller' || brandLoading.value) return;
  if (page > 1 && !brandHasMore.value) return;

  brandLoading.value = true;
  try {
    const res = await brandApi.getPartSellerBrands({
      page,
      size: brandPagination.size,
      keyword: keyword || undefined,
    });
    if (res?.success) {
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const total = res.data?.total ?? rows.length;
      mergeBrandRows(rows, reset);
      brandHasMore.value = brandOptions.value.length < total;
      brandPagination.page = page;
    } else if (reset) {
      brandOptions.value = [];
      brandHasMore.value = false;
    }
  } finally {
    brandLoading.value = false;
  }
};

const initBrandList = async (brandIds = [], brandList = []) => {
  resetBrandList();
  if (brandIds.length) {
    let rows = [...brandList];
    const existingIds = new Set(rows.map((brand) => String(brand.id ?? brand.brandId)));
    const missingIds = brandIds.filter((id) => !existingIds.has(String(id)));
    if (missingIds.length) {
      const fetchedBrands = await fetchBrandsByIds(missingIds);
      rows = [...rows, ...fetchedBrands];
    }
    seedSelectedBrandOptions(brandIds, rows);
    await fetchBrandOptions(1, '', false);
    return;
  }
  await fetchBrandOptions(1, '', true);
};

const handleBrandSearch = (value) => {
  brandSearchKeyword.value = value;
  fetchBrandOptions(1, value, true);
};

const handleBrandPopupScroll = (e) => {
  const { target } = e;
  if (
    target.scrollHeight - target.scrollTop - target.clientHeight < 80 &&
    brandHasMore.value &&
    !brandLoading.value
  ) {
    fetchBrandOptions(brandPagination.page + 1, brandSearchKeyword.value);
  }
};

const columns = computed(() => [
  {
    title: st('usersPermissionsPage.colAccountEmail'),
    dataIndex: 'delegateUserEmail',
  },
  {
    title: st('usersPermissionsPage.colUserName'),
    dataIndex: 'delegateUserName',
  },
  {
    key: 'createDate',
    title: st('usersPermissionsPage.colCreateDate'),
    dataIndex: 'createDate',
  },
  {
    width: 100,
    key: 'status',
    title: st('usersPermissionsPage.colStatus'),
    dataIndex: 'status',
  },
  {
    width: 300,
    key: 'actions',
    title: st('usersPermissionsPage.colActions'),
  },
]);

function removeUsersPermissions(menu) {
  if (!menu) return menu;
  // 如果是数组，递归处理每一项
  if (Array.isArray(menu)) {
    return menu
      .filter(item => !item.path?.includes('users-permissions'))
      .map(item => removeUsersPermissions(item));
  }
  // 如果有 children，递归处理 children
  if (menu.children) {
    menu.children = removeUsersPermissions(menu.children);
  }
  // 自己本身如果包含 path，且包含 users-permissions，则返回 null
  if (menu.path && menu.path.includes('users-permissions')) {
    return null;
  }

  return menu;
}

// 移除用户权限项，根节点展示为 all（深拷贝，避免就地改写 store 中的 menus）
const cleanedMenu = removeUsersPermissions(
  store.userInfo?.menus ? structuredClone(store.userInfo.menus) : null,
);
const menu = cleanedMenu ? { ...cleanedMenu, name: 'ALL' } : cleanedMenu;

// 代理用户列表
const data = ref([]);

// 关闭弹窗
const handleCancel = () => {
  open.value = false;
  resetFields();
}

// 保存代理用户
const handleConfirm = () => {
  validate().then((res) => {
    if (!res.errorFields) {
      loading.value = true;
      const menuIds = [...parentMenu, ...form.value.menuIds];

      if (!menuIds.includes(menu.id)) {
        menuIds.push(menu.id); // 加上根路由ID
      }

      const params = {
        email: form.value.email,
        userName: form.value.userName,
        menuIds,
      }

      if (userType === 'seller') {
        params.brandIds = form.value.brandIds || [];
      }

      const isEdit = form.value.index > -1;
      const editIndex = form.value.index;
      const delegationId = isEdit ? data.value[editIndex].delegationId : null;

      if (form.value?.index > -1) {
        delete params.index;
        delete params.email; // 编辑时, 不传email
      }

      open.value = false;
      resetFields();
      loading.value = false;

      registerApi[isEdit ? 'updateUser' : 'addUser'](delegationId, params).then((data) => {
        if (data.success) {
          parentMenu.clear();
          open.value = false;
          loading.value = false;
          message.success(isEdit ? st('usersPermissionsPage.userUpdatedSuccess') : st('usersPermissionsPage.userAddedSuccess'));
          // 重置表单
          resetFields();
          // 重新请求列表
          getUserList();
        } else {
          if (data?.message) {
            message.error(data.message);
          }
        }
      })
    }
  });
};

const handleMenuIdsChange = (value) => {
  parentMenu.clear()
  for (const item of menu.children) {
    if (item.children?.length) {
      for (const child of item.children) {
        if (value.includes(child.id)) {
          parentMenu.add(item.id);
          break;
        }
      }
    }
  }
}

// 编辑代理用户
const handleEdit = async (record, index) => {
  const { brandIds, brandList } = normalizeRecordBrands(record);
  form.value = {
    index,
    email: record.delegateUserEmail,
    menuIds: record.menuIds,
    brandIds,
    userName: record.delegateUserName,
  };
  parentMenu.clear();
  record.parentIds?.forEach((id) => parentMenu.add(id));
  open.value = true;
  await initBrandList(brandIds, brandList);
};

// 删除代理用户
const handleDelete = (record, index) => {
  loading.value = true;

  registerApi.deleteUser(record.delegationId).then((res) => {
    loading.value = false;

    if (res.success) {
      message.success(st('usersPermissionsPage.userDeletedSuccess'));
      // 不需要重新请求列表
      data.value.splice(index, 1);
    }
  })
}

// 激活、禁用代理用户
const handleStatus = (record, index) => {
  loading.value = true;

  registerApi.setUserStatus(record.delegationId, record.status === 'ACTIVE' ? 'deactivate' : 'activate').then((res) => {
    loading.value = false;

    if (res.success) {
      message.success(st('usersPermissionsPage.userStatusUpdatedSuccess'));
      data.value[index].status = record.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    }
  })
}

// 获取所有子节点路由, 返回所有子节点ID和所有父节点ID
function getAllChildrenIds(node) {
  let ids = [];
  let parentIds = [];

  if (node.children && node.children.length > 0) {
    parentIds.push(node.id);

    node.children.forEach(child => {
      const [childIds, childParentIds] = getAllChildrenIds(child);
      ids = ids.concat(childIds);
      parentIds = parentIds.concat(childParentIds);
    });
  } else {
    ids.push(node.id);
  }

  return [ids, parentIds];
}

// 获取代理用户列表
const getUserList = () => {
  loading.value = true;

  registerApi.getSubAccounts().then((res) => {
    loading.value = false;

    if (res.success) {
      data.value = res.data.map(item => {
        if (item?.menuTree) {
          const [childIds, parentIds] = getAllChildrenIds(item?.menuTree);

          item.menuIds = childIds;
          item.parentIds = parentIds;
        } else {
          item.menuIds = [];
        }

        const { brandIds, brandList } = normalizeRecordBrands(item);
        item.brandIds = brandIds;
        item.brandList = brandList;

        return item;
      });
    }
  })
}

onBeforeMount(() => {
  getUserList();
})
</script>

<style lang="less" scoped>
.users-permissions {
  padding: 16px;
  padding-top: unset;

  &-content {
    padding: 0 16px;
    border-radius: 8px;
    padding-bottom: 32px;
    background-color: var(--color-white);
    box-shadow: var(--section-box-shadow-6);
  }

  .add-new-user {
    float: right;
    margin: 16px 0;
  }

  :deep(.ant) {
    &-form-item {
      &-row {
        display: block;
      }

      &-label {
        display: block;
        text-align: left;
      }

      .ant-input {
        background: transparent;
      }
    }

    &-table {
      .ant-btn {
        min-width: auto;
        border-width: 1px;
        font-weight: normal;
      }
    }

    &-select-tree {
      &-checkbox {
        margin-block-start: 0;
        margin-inline-end: 4px;

        &-inner {
          border-radius: 4px;
        }
      }

      &-switcher-icon {
        vertical-align: middle;
      }
    }
  }

  .actions {
    display: flex;
    gap: 8px;


    .delete {
      color: var(--red-text);
      border-color: var(--red-text);
    }

    .status-btn {
      width: 97px;
    }
  }

  .brands-permission-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .help-icon {
    margin-left: 4px;
    font-size: 14px;
    color: #878394;
    cursor: pointer;
    vertical-align: -2px;
  }

  .brands-permission-helper {
    margin: 6px 0 0;
    font-family: Sora, sans-serif;
    font-size: 12px;
    line-height: 16px;
    color: rgba(15, 6, 41, 0.45);
  }
}
</style>